"use client";

import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, BadgeCheck, Layers, RefreshCw, Target, Users } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { LogoMark } from "@/components/logo";
import { Button } from "@/components/ui/button";

const EASE = [0.16, 1, 0.3, 1] as const;

const CAPABILITIES = [
  { icon: Layers, label: "Every source unified" },
  { icon: RefreshCw, label: "Real-time sync" },
  { icon: Target, label: "Source attribution" },
  { icon: BadgeCheck, label: "Built for fitness data" },
];

/* ------------------------------------------------------------------ */
/* Hero copy                                                           */
/* ------------------------------------------------------------------ */

function HeroCopy() {
  const reduceMotion = useReducedMotion();
  const item = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };
  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
  };
  const containerProps = reduceMotion
    ? {}
    : { initial: "hidden" as const, animate: "visible" as const, variants: container };
  const iv = reduceMotion ? undefined : item;

  return (
    <motion.div
      className="flex max-w-xl flex-col items-center text-center xl:items-start xl:text-left"
      {...containerProps}
    >
      <motion.span
        variants={iv}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground"
      >
        <Users className="size-4" />
        Customer Data
      </motion.span>

      <motion.h1
        variants={iv}
        className="mt-4 text-balance text-4xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-[2.75rem] lg:text-5xl"
      >
        The more you connect, the smarter Helios gets
      </motion.h1>

      <motion.p
        variants={iv}
        className="mt-6 max-w-lg text-balance leading-relaxed text-muted-foreground sm:text-lg"
      >
        Plug in the booking software you already run, and Helios pulls every lead, visit, and
        purchase into one always-current profile.
      </motion.p>

      <motion.div variants={iv} className="mt-8 w-full sm:w-auto">
        <Button asChild size="xl" className="group w-full sm:w-auto">
          <Link href="/book-a-demo">
            Book a demo
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </Button>
      </motion.div>

      <motion.div
        variants={iv}
        className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 xl:justify-start"
      >
        {CAPABILITIES.map(({ icon: Icon, label }) => (
          <span
            key={label}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground"
          >
            <Icon className="size-3.5" />
            {label}
          </span>
        ))}
      </motion.div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Sources -> unified profile diagram                                 */
/* ------------------------------------------------------------------ */

type Source = {
  name: string;
  raw: string;
  img?: string;
  logo?: boolean;
};

const SOURCES: Source[] = [
  {
    name: "Mariana Tek",
    raw: "memberships · visits",
    img: "/images/integrations/mariana-tek/icon.png",
  },
  { name: "Mindbody", raw: "purchases · classes", img: "/images/integrations/mindbody/icon.png" },
  { name: "Facebook ads", raw: "lead · utm source", img: "/images/integrations/facebook/icon.png" },
  { name: "Helios forms", raw: "name · email · goal", logo: true },
];

function SourceChip({ source }: { source: Source }) {
  return (
    <div className="flex w-[176px] items-center gap-2.5 rounded-xl border border-border bg-background px-3 py-2.5 shadow-sm">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/40">
        {source.img ? (
          <Image
            src={source.img}
            alt={source.name}
            width={18}
            height={18}
            className="size-[18px] rounded object-contain"
          />
        ) : source.logo ? (
          <LogoMark className="size-[15px]" />
        ) : null}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[12px] font-semibold leading-tight text-foreground">
          {source.name}
        </span>
        <span className="block truncate text-[10px] leading-tight text-muted-foreground">
          {source.raw}
        </span>
      </span>
    </div>
  );
}

function ProfileRow({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <span className="text-[12px] text-muted-foreground">{label}</span>
      {accent ? (
        <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--brand-accent)/0.35)] bg-[hsl(var(--brand-accent)/0.08)] px-2 py-0.5 text-[11px] font-medium text-foreground">
          <Image
            src="/images/integrations/facebook/icon.png"
            alt="Facebook"
            width={12}
            height={12}
            className="size-3 rounded-sm object-contain"
          />
          {value}
        </span>
      ) : (
        <span className="text-[12px] font-semibold text-foreground">{value}</span>
      )}
    </div>
  );
}

function ProfileCard({ reduce }: { reduce: boolean }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-xl">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border px-4 py-3.5">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-accent)/0.12)] text-sm font-semibold text-foreground">
          MC
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">Maya Chen</p>
          <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-success/10 px-1.5 py-0.5 text-[10px] font-medium text-success">
            <BadgeCheck className="size-3" />
            Active member
          </span>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1.5 text-[10px] font-medium text-muted-foreground">
          <span className="relative flex size-1.5">
            {!reduce ? (
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-75" />
            ) : null}
            <span className="relative inline-flex size-1.5 rounded-full bg-success" />
          </span>
          Synced now
        </span>
      </div>

      {/* Normalized fields */}
      <div className="divide-y divide-border/70">
        <ProfileRow label="Membership" value="Unlimited" />
        <ProfileRow label="Lifetime visits" value="142" />
        <ProfileRow label="Total spend" value="$2,480" />
        <ProfileRow label="Intro offer" value="Converted" />
        <ProfileRow label="First touch" value="spring-promo" accent />
      </div>

      {/* Footer */}
      <div className="flex items-center gap-1.5 border-t border-border bg-muted/30 px-4 py-2.5">
        <Layers className="size-3.5 text-muted-foreground" />
        <span className="text-[11px] text-muted-foreground">Unified from 4 sources</span>
      </div>
    </div>
  );
}

// Connector geometry for the xl diagram (coordinate space = 560 x 420).
const CARD_ANCHOR = { x: 260, y: 210 } as const;
const CHIP_Y = [60, 160, 260, 360] as const;
const CHIP_RIGHT_X = 176;

function Connectors({ reduce }: { reduce: boolean }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 560 420"
      fill="none"
      className="pointer-events-none absolute inset-0 h-full w-full"
      preserveAspectRatio="none"
    >
      {CHIP_Y.map((y, i) => {
        const d = `M${CHIP_RIGHT_X},${y} C ${CHIP_RIGHT_X + 44},${y} ${CARD_ANCHOR.x - 44},${CARD_ANCHOR.y} ${CARD_ANCHOR.x},${CARD_ANCHOR.y}`;
        return (
          <g key={y}>
            <path d={d} stroke="hsl(var(--border))" strokeWidth={1.5} />
            {!reduce ? (
              <motion.path
                d={d}
                stroke="hsl(var(--brand-accent))"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeDasharray="5 150"
                initial={{ strokeDashoffset: 155 }}
                animate={{ strokeDashoffset: 0 }}
                transition={{
                  duration: 1.8,
                  ease: "linear",
                  repeat: Number.POSITIVE_INFINITY,
                  delay: i * 0.45,
                }}
              />
            ) : null}
          </g>
        );
      })}
      {/* Merge node at the card edge */}
      <circle cx={CARD_ANCHOR.x} cy={CARD_ANCHOR.y} r={4} fill="hsl(var(--brand-accent))" />
      <circle
        cx={CARD_ANCHOR.x}
        cy={CARD_ANCHOR.y}
        r={7}
        fill="hsl(var(--brand-accent))"
        opacity={0.15}
      />
    </svg>
  );
}

function Stage() {
  const reduce = useReducedMotion() ?? false;
  const fade = reduce
    ? {}
    : {
        initial: { opacity: 0, x: 24, scale: 0.98 },
        animate: { opacity: 1, x: 0, scale: 1 },
        transition: { duration: 0.8, ease: EASE, delay: 0.4 },
      };
  const railFade = reduce
    ? {}
    : {
        initial: { opacity: 0, x: -16 },
        animate: { opacity: 1, x: 0 },
        transition: { duration: 0.8, ease: EASE, delay: 0.55 },
      };

  return (
    <div className="relative w-full">
      {/* xl: full sources -> profile diagram */}
      <div className="relative mx-auto hidden h-[420px] w-[560px] xl:block">
        <Connectors reduce={reduce} />

        <motion.div aria-hidden className="absolute inset-0" {...railFade}>
          {SOURCES.map((source, i) => (
            <div
              key={source.name}
              className="absolute -translate-y-1/2"
              style={{ left: 0, top: CHIP_Y[i] }}
            >
              <SourceChip source={source} />
            </div>
          ))}
        </motion.div>

        <div className="absolute right-0 top-1/2 w-[300px] -translate-y-1/2">
          <motion.div {...fade}>
            <ProfileCard reduce={reduce} />
          </motion.div>
        </div>
      </div>

      {/* < xl: just the unified profile */}
      <motion.div className="mx-auto w-[300px] sm:w-[340px] xl:hidden" {...fade}>
        <ProfileCard reduce={reduce} />
      </motion.div>
    </div>
  );
}

export function CustomerDataHero() {
  return (
    <section className="relative overflow-x-clip border-b border-border bg-background pb-16 pt-28 sm:pb-20 lg:pt-36">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 lg:px-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,600px)] xl:gap-10">
        <HeroCopy />
        <Stage />
      </div>
    </section>
  );
}
